import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, ShieldCheck, Menu, X, RefreshCw } from "lucide-react";
import { refreshLiveData } from "@/hooks/useLiveRefresh";
import { useAuth } from "@/context/AuthContext";
import { NAV, canSee, ROLE_LABELS } from "@/lib/rbac";
import { GlobalSearch } from "@/components/v3/GlobalSearch";

function SidebarContent({ role, user, onLogout, onNavigate }) {
  return (
    <>
      <div className="h-1 bg-[#b3122a]" />
      <div className="px-5 pt-5 pb-4 border-b border-white/10">
        <div className="bg-white px-3 py-2.5 flex items-center justify-center">
          <img src="/redfort-logo.gif" alt="Red Fort Capital" className="h-8 w-auto object-contain" />
        </div>
        <div className="mt-3 flex items-baseline justify-between">
          <span className="font-display text-white text-[15px] tracking-tight" data-testid="sidebar-product-name">Loan <i className="text-[#ff8fa0]">Pulse</i></span>
          <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-slate-500">Est. 2019 · Delhi</span>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {NAV.map((g) => {
          const items = g.items.filter((it) => canSee(role, it));
          if (!items.length) return null;
          return (
            <div key={g.group} className="mb-4">
              <div className="px-3 mb-1.5 text-[9px] font-bold uppercase tracking-[0.24em] text-slate-500 flex items-center gap-2"><span className="w-3 h-px bg-slate-600" />{g.group}</div>
              <div className="space-y-0.5">
                {items.map((it) => (
                  <NavLink key={it.to} to={it.to} end={it.to === "/"} data-testid={it.testid}
                    onClick={onNavigate}
                    className={({ isActive }) => `rf-nav ${isActive ? "active" : ""}`}>
                    <it.icon size={16} />
                    <span className="flex-1">{it.label}</span>
                    {it.build && <span className="text-[9px] font-bold text-slate-500 border border-white/10 rounded px-1 py-px">B{it.build}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="px-3 pb-4">
        <div className="border border-white/10 p-3 mb-2">
          <div className="flex items-center gap-2 text-slate-300 text-sm">
            <ShieldCheck size={15} className="text-[#b3122a]" />
            <span className="truncate font-semibold text-white" data-testid="sidebar-user-name">{user?.name || "User"}</span>
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5 truncate">{user?.email}</div>
          <div className="mt-1.5 inline-block bg-[#b3122a]/20 text-[#ff8fa0] rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider" data-testid="sidebar-user-role">
            {ROLE_LABELS[user?.role] || user?.role || "—"}
          </div>
        </div>
        <button onClick={onLogout} data-testid="logout-button" className="rf-nav w-full text-left">
          <LogOut size={16} /> Sign out
        </button>
      </div>
    </>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const role = user?.role || "admin";
  const internal = role !== "dsa" && role !== "rm";

  const handleLogout = () => { logout(); navigate("/login"); };

  if (role === "borrower") {
    return (
      <div className="min-h-screen bg-[#F4F1EA]" data-testid="portal-layout">
        <div className="h-1 bg-[#b3122a]" />
        <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-4" data-testid="portal-top-bar">
          <img src="/redfort-logo.gif" alt="Red Fort Capital" className="h-7 w-auto" />
          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">Loan Pulse · Borrower portal</span>
          <span className="ml-auto text-[12px] text-slate-600" data-testid="portal-user-email">{user?.email}</span>
          <button onClick={handleLogout} data-testid="logout-button" className="rf-btn-ghost"><LogOut size={13} strokeWidth={1.5} /> Sign out</button>
        </header>
        <main><Outlet /></main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#F4F1EA]">
      <aside className="w-[248px] shrink-0 bg-[#111111] text-white flex-col fixed h-screen z-40 hidden lg:flex" data-testid="sidebar">
        <SidebarContent role={role} user={user} onLogout={handleLogout} />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-[268px] bg-[#111111] text-white flex flex-col" data-testid="mobile-sidebar">
            <button onClick={() => setMobileOpen(false)} data-testid="mobile-sidebar-close" className="absolute top-3 right-3 text-slate-400"><X size={18} /></button>
            <SidebarContent role={role} user={user} onLogout={handleLogout} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="app-content flex-1 lg:ml-[248px] min-h-screen flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-[#F4F1EA]/95 backdrop-blur-[2px] border-b border-slate-200 px-4 lg:px-6 py-2.5 flex items-center gap-3" data-testid="top-bar">
          <button className="lg:hidden text-slate-600" data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)}><Menu size={20} /></button>
          {internal ? <GlobalSearch /> : <div className="flex-1" />}
          <div className="flex-1" />
          <button type="button" data-testid="refresh-visible-data" aria-label="Refresh visible data" title="Refresh visible data" onClick={refreshLiveData} className="p-2 text-slate-600 hover:text-[#b3122a] transition-colors shrink-0"><RefreshCw size={16} /></button>
          <div
            data-testid="internal-test-badge"
            className="flex items-center gap-2 bg-[#111111] text-[#F4F1EA] border border-[#111111] px-2.5 py-1 select-none shrink-0"
            title="Internal testing environment. No production cutover has been authorized."
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#b3122a] animate-pulse" />
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] whitespace-nowrap">Internal · Preview</span>
          </div>
        </header>
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
